/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Search, Send, Loader2, CheckCircle2, FileText, Pill, ShieldCheck, Stethoscope } from 'lucide-react';
import api from '@/lib/api';
import { requestSecondOpinion } from '@/lib/marketplace';
import { cn } from '@/lib/utils';
import { initials } from '@/lib/utils-cat';

export const RequestForm = ({ onDone }) => {
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [selected, setSelected] = useState(null);
  const [question, setQuestion] = useState('');
  const [includePrescriptions, setIncludePrescriptions] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [urgency, setUrgency] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/telemedicine/doctors').then((r) => {
      const list = r.data?.doctors || r.data || [];
      setDoctors(Array.isArray(list) ? list : []);
    }).catch((err) => {
      console.error('SecondOpinion: failed to load doctors', err);
    }).finally(() => setLoadingDoctors(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter((d) =>
      (d.full_name || '').toLowerCase().includes(q) ||
      (d.specialty || '').toLowerCase().includes(q)
    );
  }, [doctors, search]);

  const submit = async () => {
    if (!selected) { toast.error('Seleziona un medico destinatario'); return; }
    if (question.trim().length < 10) { toast.error('Descrivi meglio il quesito clinico (min 10 caratteri)'); return; }
    setSubmitting(true);
    try {
      const res = await requestSecondOpinion({
        target_doctor_id: selected.id,
        clinical_question: question.trim(),
        include_prescriptions: includePrescriptions,
        include_notes: includeNotes,
        urgency,
      });
      toast.success('Richiesta di secondo parere inviata');
      onDone?.(res.second_opinion);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio richiesta');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-primary" />Nuova richiesta di secondo parere</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Cerca medico</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome o specialità..." className="pl-9" data-testid="so-search-doctor" />
          </div>
          <div className="mt-2 max-h-56 overflow-y-auto scrollbar-thin border border-border/60 rounded-xl">
            {loadingDoctors ? (
              <div className="p-3 space-y-2"><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground">Nessun medico trovato.</div>
            ) : filtered.slice(0, 20).map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setSelected(d)}
                className={cn(
                  'w-full flex items-center gap-2 p-2.5 text-left hover:bg-accent/40 transition-colors border-b border-border/40 last:border-0',
                  selected?.id === d.id && 'bg-primary/10'
                )}
                data-testid={`so-doctor-${d.id}`}
              >
                <Avatar className="h-8 w-8"><AvatarFallback className="text-xs">{initials(d.full_name || '?')}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{d.full_name}</div>
                  {d.specialty && <div className="text-[11px] text-muted-foreground truncate">{d.specialty}</div>}
                </div>
                {selected?.id === d.id && <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="so-question">Quesito clinico</Label>
          <Textarea id="so-question" rows={5} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Descrivi la tua situazione clinica, la diagnosi ricevuta, la terapia proposta e cosa vorresti chiarire..." data-testid="so-question-input" />
          <div className="text-[11px] text-muted-foreground mt-1">{question.length}/5000</div>
        </div>

        <div className="grid gap-3">
          <div className="flex items-center justify-between rounded-xl border border-border/60 p-3">
            <div className="flex items-center gap-2">
              <Pill className="h-4 w-4 text-primary" />
              <div>
                <div className="text-sm font-medium">Includi le tue ricette recenti</div>
                <div className="text-[11px] text-muted-foreground">Il medico vedrà le ultime 10 ricette.</div>
              </div>
            </div>
            <Switch checked={includePrescriptions} onCheckedChange={setIncludePrescriptions} data-testid="so-include-prescriptions" />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border/60 p-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <div>
                <div className="text-sm font-medium">Includi note della visita originale</div>
                <div className="text-[11px] text-muted-foreground">Richiede un ID appuntamento (in questa versione: opzionale).</div>
              </div>
            </div>
            <Switch checked={includeNotes} onCheckedChange={setIncludeNotes} data-testid="so-include-notes" />
          </div>
          <div>
            <Label>Urgenza</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger data-testid="so-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Bassa</SelectItem>
                <SelectItem value="normal">Normale</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Alert>
          <ShieldCheck className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Confermando l'invio, autorizzi il medico selezionato ad accedere ai dati clinici allegati (se selezionati). L'accesso è tracciato negli audit log.
          </AlertDescription>
        </Alert>

        <Button onClick={submit} disabled={submitting} className="w-full" data-testid="so-submit-btn">
          {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Invio...</> : <><Send className="h-4 w-4" /> Invia richiesta</>}
        </Button>
      </CardContent>
    </Card>
  );
};
